<template>
  <div class="map-page-wrapper">
    <div v-if="isLoading && !uiStore.isEditingDefense" class="loading-overlay">
      <h1><i class="fas fa-spinner fa-spin"></i> Loading City Map...</h1>
    </div>

    <GameSiegeSetup
      v-else-if="uiStore.isEditingDefense"
      :cells="gameStore.defenseDraftCells"
      :selected-slug="gameStore.defenseSelectedSlug"
      :place-qty="gameStore.defensePlaceQty"
      @cell-click="onDefenseCellClick"
    />

    <ProgressionMap
      v-else-if="progression"
      :progressionData="progression"
      :playerRace="gameStore.raceLabelKey"
      :socketArtByNode="gameStore.socketArtByNodeKey"
      @node-selected="handleNodeSelect"
    />

    <div v-else class="loading-overlay">
      <h1><i class="fas fa-exclamation-triangle"></i> Error loading map.</h1>
    </div>
    <NodeModal
      :isVisible="isModalVisible"
      :nodeData="selectedNodeData"
      :playerRace="gameStore.raceLabelKey"
      :isLoading="isUnlocking"
      :errorMessage="modalErrorMessage"
      :enableCardSocketing="true"
      @close="isModalVisible = false"
      @confirm-unlock="handleConfirmUnlock"
      @socket-card="handleSocketCard"
      @unsocket-card="handleUnsocketCard"
    />
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, computed, ref, watch } from "vue";
import { useGameStore } from "../store/game";
import { useUiStore } from "../store/ui";
import ProgressionMap from "../components/ProgressionMap.vue";
import GameSiegeSetup from "./GameSiegeSetup.vue";
import NodeModal from "../components/NodeModal.vue";

const gameStore = useGameStore();
const uiStore = useUiStore();

// Get data from the store
const progression = computed(() => gameStore.cityProgression);
const isLoading = computed(() => gameStore.isLoading); // For map loading

// Modal State
const isModalVisible = ref(false);
const selectedNodeData = ref(null); // Will hold { node, status }
const isUnlocking = ref(false); // Loading state for the unlock action
const modalErrorMessage = ref(""); // Error from unlock action

// Fetch data on mount
onMounted(async () => {
  if (!gameStore.cityProgression) {
    await gameStore.fetchCityProgression();
  }
  await gameStore.fetchPlayerAssets();
  if (gameStore.activeCity) {
    await Promise.all([
      gameStore.fetchPlayerCards(),
      gameStore.fetchCitySockets(gameStore.activeCity.id),
      gameStore.fetchCityDefense(gameStore.activeCity.id),
      gameStore.fetchActiveQuests(),
    ]);
    await gameStore.fetchAllHeroSockets();
  }
});

onUnmounted(() => {
  gameStore.cancelDefenseEdit();
});

watch(
  () => uiStore.isEditingDefense,
  (editing) => {
    if (editing) {
      isModalVisible.value = false;
      selectedNodeData.value = null;
    }
  }
);

const onDefenseCellClick = ({ row, col }) => {
  gameStore.placeDefenseStack(row, col);
};

// Show modal when a node is selected
const handleNodeSelect = (data) => {
  modalErrorMessage.value = "";
  if (data && data.node) {
    selectedNodeData.value = data;
    isModalVisible.value = true;
  } else {
    selectedNodeData.value = null;
    isModalVisible.value = false;
  }
};

// Handle the unlock/found confirmation
const handleConfirmUnlock = async (nodeKey, extra = {}) => {
  if (!nodeKey) return;
  modalErrorMessage.value = ""; // Clear previous errors
  isUnlocking.value = true;

  try {
    // --- Check if this is the special "Found City" node ---
    if (nodeKey === "FoundCity") {
      await gameStore.foundCity(nodeKey, extra.cityName);
    } else {
      // --- This is for unlocking REGULAR nodes ---
      await gameStore.unlockCityNode(nodeKey);
    }

    // --- If either action was successful ---
    isModalVisible.value = false; // Close modal
    selectedNodeData.value = null; // Clear selection
  } catch (error) {
    modalErrorMessage.value = error.message; // Show error in modal
  } finally {
    isUnlocking.value = false; // Stop loading indicator
  }
};

const handleSocketCard = async ({ nodeKey, playerCardId }) => {
  if (!nodeKey || !playerCardId || !gameStore.activeCity) return;
  modalErrorMessage.value = "";
  isUnlocking.value = true;
  try {
    await gameStore.socketCityCard(
      gameStore.activeCity.id,
      nodeKey,
      playerCardId
    );
  } catch (error) {
    modalErrorMessage.value =
      error.response?.data?.message || error.message || "Failed to socket card.";
  } finally {
    isUnlocking.value = false;
  }
};

const handleUnsocketCard = async ({ nodeKey }) => {
  if (!nodeKey || !gameStore.activeCity) return;
  modalErrorMessage.value = "";
  isUnlocking.value = true;
  try {
    await gameStore.unsocketCityCard(gameStore.activeCity.id, nodeKey);
  } catch (error) {
    modalErrorMessage.value =
      error.response?.data?.message ||
      error.message ||
      "Failed to unsocket card.";
  } finally {
    isUnlocking.value = false;
  }
};

// Log data changes (for debugging)
watch(progression, (newData) => {
  console.log("--- GameCity.vue ---");
  console.log("Progression data received from store:", newData);
});
</script>

<style scoped>
.map-page-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
  background: transparent;
}

.loading-overlay {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #00f6ff;
  font-family: "Orbitron", sans-serif;
  /* Add a backdrop so text is readable on the image */
  background-color: rgba(10, 10, 20, 0.7);
  backdrop-filter: blur(5px);
}
</style>
